import { jsonToUrlParams } from '@/utils';

import api from '.';
import {
	IDeviceCheckRequest,
	IDeviceCheckResponse,
	IDeviceClientLoginRequest,
	IDeviceClientLoginResponse,
	IDeviceMobileApproveResponse,
	IDeviceMobileDetailResponse,
	IDeviceMobileReapproveResponse,
	IDeviceMobileRegisterRequest,
	IDeviceMobileRegisterResponse,
	IDeviceMobileRejectRequest,
	IDeviceMobileRejectResponse,
	IDeviceMobileRevokeResponse,
	IDeviceTabletActivateRequest,
	IDeviceTabletActivateResponse,
	IDeviceTabletDeactivateResponse,
	IDeviceTabletDetailResponse,
	IDeviceTabletRegisterCodeRequest,
	IDeviceTabletRegisterCodeResponse,
	IDeviceTabletRemoteCodeResponse,
	IDeviceTabletVerifyRegisterCodeRequest,
	IDeviceTabletVerifyRegisterCodeResponse,
	IDeviceTabletVerifyRemoteCodeRequest,
	IDeviceTabletVerifyRemoteCodeResponse,
	IGetDeviceLogListParams,
	IGetDeviceMobileListParams,
	IGetDeviceTabletListParams,
	IGetTabletActivationsParams,
	TGetDeviceLogListResponse,
	TGetDeviceMobileListResponse,
	TGetDeviceTabletListResponse,
	TGetTabletActivationsResponse,
} from './api.type';

/* ---- DEVICE MOBILE START---- */
export const getDeviceMobileList = async ({
	page,
	pageSize,
	sortSql = '',
	...rest
}: IGetDeviceMobileListParams) =>
	await api.post<TGetDeviceMobileListResponse>(
		`worker/device/mobile/list?${jsonToUrlParams({ page, pageSize, sortSql })}`,
		rest
	);

/**
 * Get mobile device detail - Get detailed info including recent logs
 */
export const getMobileDeviceDetail = async (deviceId: number) =>
	await api.get<IDeviceMobileDetailResponse>(
		`worker/device/mobile/${deviceId}`
	);
/* ---- DEVICE MOBILE END---- */

/* ---- DEVICE MOBILE ADMIN API START---- */
/**
 * Approve mobile device - Activate device token
 */
export const approveMobileDevice = async (deviceId: number) =>
	await api.post<IDeviceMobileApproveResponse>(
		`worker/device/mobile/${deviceId}/approve`
	);

/**
 * Reject mobile device - Reject pending device with reason
 */
export const rejectMobileDevice = async (
	deviceId: number,
	params: IDeviceMobileRejectRequest
) =>
	await api.post<IDeviceMobileRejectResponse>(
		`worker/device/mobile/${deviceId}/reject`,
		params
	);

/**
 * Revoke mobile device - Revoke approved device access
 */
export const revokeMobileDevice = async (deviceId: number) =>
	await api.post<IDeviceMobileRevokeResponse>(
		`worker/device/mobile/${deviceId}/revoke`
	);

/**
 * Reapprove mobile device - Reapprove revoked device
 */
export const reapproveMobileDevice = async (deviceId: number) =>
	await api.post<IDeviceMobileReapproveResponse>(
		`worker/device/mobile/${deviceId}/reapprove`
	);
/* ---- DEVICE MOBILE ADMIN API END---- */

/* ---- DEVICE TABLET START---- */
export const getDeviceTabletList = async ({
	page,
	pageSize,
	sortSql = '',
	...rest
}: IGetDeviceTabletListParams) =>
	await api.post<TGetDeviceTabletListResponse>(
		`worker/device/tablet/list?${jsonToUrlParams({ page, pageSize, sortSql })}`,
		rest
	);

/**
 * Get tablet device detail - Get detailed info including activation history and recent logs
 */
export const getTabletDeviceDetail = async (tabletId: number) =>
	await api.get<IDeviceTabletDetailResponse>(
		`worker/device/tablet/${tabletId}`
	);
/* ---- DEVICE TABLET END---- */

/* ---- DEVICE TABLET ADMIN API START---- */
/**
 * Create tablet registration code - Generate 6-digit code for new tablet registration
 */
export const createTabletRegisterCode = async (
	params: IDeviceTabletRegisterCodeRequest
) =>
	await api.post<IDeviceTabletRegisterCodeResponse>(
		'worker/device/tablet/register-code',
		params
	);

/**
 * Regenerate tablet registration code - Invalidate old code and create new one
 */
export const regenerateTabletRegisterCode = async (codeId: number) =>
	await api.post<IDeviceTabletRegisterCodeResponse>(
		`worker/device/tablet/register-code/${codeId}/regenerate`
	);

/**
 * Revoke tablet registration code - Invalidate the pending code (admin closes
 * the create-code popup before tablet finishes registration). Without this,
 * the code stays valid until natural 5-min expiry and a tablet entering it
 * later hits the network check first → DM_NOT_IN_OFFICE instead of
 * DM_INVALID_CODE.
 */
export const revokeTabletRegisterCode = async (codeId: number) =>
	await api.post(`worker/device/tablet/register-code/${codeId}/revoke`);

/**
 * Activate tablet - Enable external access for tablet
 */
export const activateTablet = async (
	tabletId: number,
	params: IDeviceTabletActivateRequest
) =>
	await api.post<IDeviceTabletActivateResponse>(
		`worker/device/tablet/${tabletId}/activate`,
		params
	);

/**
 * Deactivate tablet - Disable external access immediately
 */
export const deactivateTablet = async (tabletId: number) =>
	await api.post<IDeviceTabletDeactivateResponse>(
		`worker/device/tablet/${tabletId}/deactivate`
	);

/**
 * Delete tablet - Logical delete tablet from system
 */
export const deleteTablet = async (tabletId: number) =>
	await api.delete(`worker/device/tablet/${tabletId}`);

/**
 * Issue remote code - Generate code for remote token re-issue
 */
export const issueTabletRemoteCode = async (tabletId: number) =>
	await api.post<IDeviceTabletRemoteCodeResponse>(
		`worker/device/tablet/${tabletId}/remote-code`
	);

/**
 * Get tablet activation history - Get paginated list of activation records
 */
export const getTabletActivations = async (
	tabletId: number,
	{ page = 1, pageSize = 20 }: IGetTabletActivationsParams = {}
) =>
	await api.get<TGetTabletActivationsResponse>(
		`worker/device/tablet/${tabletId}/activations?${jsonToUrlParams({ page, pageSize })}`
	);
/* ---- DEVICE TABLET ADMIN API END---- */

/* ---- DEVICE LOG START---- */
export const getDeviceLogList = async ({
	page,
	pageSize,
	sortSql = '',
	...rest
}: IGetDeviceLogListParams) =>
	await api.post<TGetDeviceLogListResponse>(
		`worker/device/log/list?${jsonToUrlParams({ page, pageSize, sortSql })}`,
		rest
	);
/* ---- DEVICE LOG END---- */

/* ---- DEVICE CLIENT API START---- */
/**
 * Check device status - Called first when accessing from mobile/tablet
 * Returns device status and which screen to show
 */
export const checkDeviceStatus = async (params: IDeviceCheckRequest) =>
	await api.post<IDeviceCheckResponse>('/device/client/check', params);

/**
 * Register mobile device - Request registration with admin credentials
 */
export const registerMobileDevice = async (
	params: IDeviceMobileRegisterRequest
) =>
	await api.post<IDeviceMobileRegisterResponse>(
		'/device/client/mobile/register',
		params
	);

/**
 * Verify tablet registration code - Complete tablet asset registration
 */
export const verifyTabletRegisterCode = async (
	params: IDeviceTabletVerifyRegisterCodeRequest
) =>
	await api.post<IDeviceTabletVerifyRegisterCodeResponse>(
		'/device/client/tablet/verify-register-code',
		params
	);

/**
 * Verify tablet remote code - Re-issue device token remotely
 */
export const verifyTabletRemoteCode = async (
	params: IDeviceTabletVerifyRemoteCodeRequest
) =>
	await api.post<IDeviceTabletVerifyRemoteCodeResponse>(
		'/device/client/tablet/verify-remote-code',
		params
	);

/**
 * Device client login - Login after device authentication
 */
export const deviceClientLogin = async (params: IDeviceClientLoginRequest) =>
	await api.post<IDeviceClientLoginResponse>('/device/client/login', params);
/* ---- DEVICE CLIENT API END---- */
